import React from 'react';
import PropTypes from 'prop-types';
import {get as _get} from 'lodash';
import {Button, Table} from 'semantic-ui-react';

import {DISTRIBUTION_INVOICE_STATUS} from '@constants/resources';
import {IconInfoCircle, IconInfoCircleChangelog} from '@appComponents/IconCollection';
import Form from '@appComponents/ReduxFormControls';
import SegmentHeader from '@appComponents/SegmentHeader';
import {Alert} from '@ui';
import {GetDistributionInvoiceStatusesForForm} from '@graphql/distributionInvoiceStatus/query';
import {
    BulkCreateDistributionInvoiceStatuses,
    BulkUpdateDistributionInvoiceStatuses,
} from '@graphql/distributionInvoiceStatus/mutation';
import * as messages from '@constants/messages';
import {EventContentVariantPopup} from '@utils/eventContentVariant/EventContentVariantPopup';

import {
    AUTO_UPDATE_TOOLTIP,
    INHERIT_ON_BOOKINGS_TOOLTIP,
    INVOICE_STATUS_TOOLTIP,
} from '../constants/tooltipMessages';
import {SetAllBettingNonInvoiceable} from '../components/SetAllBettingNonInvoiceable';
import validate, {validateInvoiceStatus} from './validatorDistributionInvoiceStatuses';

const UNDEFINED_STATUS_WARNING = 'It seems that the event has ended but some distributions have no invoice status. '
    + 'Please make sure that all distributions listed below have an invoice status.';
const EMPTY_STATE_MESSAGE = 'This event does not contain any distribution with invoicing support.';

const COLUMN_COUNT = 5;

const labelWithTooltip = (text, tooltip) => (
    <span>
        {text}
        {' '}
        <IconInfoCircle tooltip={tooltip}/>
    </span>
);

const getEventContentHeaderText = (eventContent) => {
    const namePart = eventContent.name ? ` - ${eventContent.name}` : '',
        mainPart = eventContent.is_main_content ? ' - Main' : '';

    return `${_get(eventContent, 'event_content_type.name', '')}${namePart}${mainPart} (${eventContent.id})`;
};

const getDistributionLabelText = (distribution) => {
    const namePart = distribution.name ? ` - ${distribution.name}` : '';

    return `${_get(distribution, 'product.short_name', '')} `
        + `${_get(distribution, 'distribution_type.name', '')}${namePart} (${distribution.id})`;
};

const renderHiddenFields = (index, distribution, data) => (
    <>
        <Form.FormField
            component={Form.SemanticInput}
            defaultValue={data ? data.id : ''}
            hidden={true}
            name={`invoice_statuses.${index}.id`}
        />
        <Form.FormField
            component={Form.SemanticInput}
            defaultValue={distribution.id}
            hidden={true}
            name={`invoice_statuses.${index}.distribution`}
        />
    </>
);

const renderDistributionRow = (
    {index, distribution, is_reportable: isReportable, invoice_status: invoiceStatus},
    options,
    autoUpdateValues,
    invoiceStatusValues,
    timestamps,
    isSectionEnd
) => {
    const data = _get(distribution, 'invoice_status', null),
        defaultAutoUpdate = data ? Boolean(data.auto_update) : true,
        defaultInheritOnBookings = data ? Boolean(data.inherit_on_bookings) : false,
        autoUpdate = (undefined === autoUpdateValues[index]) ? defaultAutoUpdate : Boolean(autoUpdateValues[index]),
        isDropdownDisabled = isReportable && autoUpdate,
        savedInvoiceStatus = _get(data, 'invoice_status.id', ''),
        invoiceStatusDefault = (undefined === invoiceStatusValues[index])
            ? (savedInvoiceStatus || (autoUpdate ? (invoiceStatus ?? '') : ''))
            : invoiceStatusValues[index];

    return (
        <Table.Row
            key={`distributionInvoiceStatus${distribution.id}`}
            className={isSectionEnd ? 'distribution-invoice-status-table__section-end' : undefined}
        >
            <Table.Cell textAlign='right'>
                {getDistributionLabelText(distribution)}
                {renderHiddenFields(index, distribution, data)}
            </Table.Cell>
            <Table.Cell textAlign='left'>
                <Form.FormField
                    component={Form.SemanticSelect}
                    name={`invoice_statuses.${index}.invoice_status`}
                    options={options}
                    defaultValue={invoiceStatusDefault}
                    timestamp={timestamps[index]}
                    placeholder='- Undefined -'
                    disabled={isDropdownDisabled}
                    required={!isDropdownDisabled}
                    validate={validateInvoiceStatus}
                />
            </Table.Cell>
            <Table.Cell textAlign='center'>
                {isReportable && (
                    <Form.FormField
                        component={Form.SemanticInput}
                        type='toggle'
                        name={`invoice_statuses.${index}.auto_update`}
                        defaultValue={defaultAutoUpdate}
                    />
                )}
            </Table.Cell>
            <Table.Cell textAlign='center'>
                {isReportable && (
                    <Form.FormField
                        component={Form.SemanticInput}
                        type='toggle'
                        name={`invoice_statuses.${index}.inherit_on_bookings`}
                        defaultValue={defaultInheritOnBookings}
                    />
                )}
            </Table.Cell>
            <Table.Cell textAlign='center'>
                {data && (
                    <IconInfoCircleChangelog
                        tooltipProps={{hoverable: true, position: 'top left'}}
                        resources={DISTRIBUTION_INVOICE_STATUS}
                        data={data}
                    />
                )}
            </Table.Cell>
        </Table.Row>
    );
};

const renderColumnHeaders = () => (
    <Table.Header>
        <Table.Row>
            <Table.HeaderCell textAlign='right'/>
            <Table.HeaderCell textAlign='left'>
                {labelWithTooltip('Invoice status', INVOICE_STATUS_TOOLTIP())}
            </Table.HeaderCell>
            <Table.HeaderCell textAlign='center'>
                {labelWithTooltip('Auto-update', AUTO_UPDATE_TOOLTIP())}
            </Table.HeaderCell>
            <Table.HeaderCell textAlign='center'>
                {labelWithTooltip('Inherit on bookings', INHERIT_ON_BOOKINGS_TOOLTIP())}
            </Table.HeaderCell>
            <Table.HeaderCell textAlign='center'>Details</Table.HeaderCell>
        </Table.Row>
    </Table.Header>
);

export default function DistributionInvoiceStatusModel() {
    const renderDistributionInvoiceStatusFields = (props) => {
        const groups = _get(props, 'groups', null),
            options = _get(props, 'options', []) || [],
            autoUpdateValues = _get(props, 'autoUpdateValues', []) || [],
            invoiceStatusValues = _get(props, 'invoiceStatusValues', []) || [],
            timestamps = _get(props, 'timestamps', []) || [],
            showWarning = _get(props, 'showWarning', false),
            isEmpty = _get(props, 'isEmpty', false),
            setAllBettingButton = (
                <SetAllBettingNonInvoiceable
                    groups={groups}
                    submitting={Boolean(_get(props, 'meta.submitting', false))}
                    eventId={props.eventId}
                    createEntity={props.createEntity}
                    updateEntity={props.updateEntity}
                    entityLabel={props.entityLabel}
                    messageBoxName={props.messageBoxName}
                />
            );

        if (null === groups) {
            return null;
        }

        return (
            <>
                <SegmentHeader buttons={setAllBettingButton}>
                    Invoice status
                </SegmentHeader>
                {showWarning && <Alert icon={false} severity='warning'>{UNDEFINED_STATUS_WARNING}</Alert>}
                {isEmpty ? (
                    <p>{EMPTY_STATE_MESSAGE}</p>
                ) : (
                    <Table basic='very' compact unstackable className='distribution-invoice-status-table'>
                        {renderColumnHeaders()}
                        <Table.Body>
                            {groups.map(({event_content: eventContent, rows}) => (
                                <React.Fragment key={`eventContent${eventContent.id}`}>
                                    <Table.Row>
                                        <Table.Cell textAlign='right'/>
                                        <Table.Cell colSpan={COLUMN_COUNT - 1} textAlign='left'>
                                            <strong>{getEventContentHeaderText(eventContent)}</strong>
                                            <EventContentVariantPopup eventContentVariant={eventContent.event_content_variant}/>
                                        </Table.Cell>
                                    </Table.Row>
                                    {rows.map((row, rowIndex) => renderDistributionRow(
                                        row,
                                        options,
                                        autoUpdateValues,
                                        invoiceStatusValues,
                                        timestamps,
                                        rowIndex === rows.length - 1
                                    ))}
                                </React.Fragment>
                            ))}
                        </Table.Body>
                        {!props.disabled && (
                            <Table.Footer>
                                <Table.Row>
                                    <Table.Cell textAlign='right'/>
                                    <Table.Cell textAlign='left'>
                                        <Button
                                            type='submit'
                                            color='blue'
                                            content='Save'
                                            icon='save'
                                            disabled={Boolean(_get(props, 'meta.submitting', false))}
                                            loading={Boolean(_get(props, 'meta.submitting', false))}
                                        />
                                    </Table.Cell>
                                    <Table.Cell textAlign='center'/>
                                    <Table.Cell textAlign='center'/>
                                    <Table.Cell textAlign='center'/>
                                </Table.Row>
                            </Table.Footer>
                        )}
                    </Table>
                )}
            </>
        );
    };

    renderDistributionInvoiceStatusFields.propTypes = {
        autoUpdateValues: PropTypes.array,
        groups: PropTypes.array,
        timestamps: PropTypes.array,
        invoiceStatusValues: PropTypes.array,
        isEmpty: PropTypes.bool,
        eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        createEntity: PropTypes.func,
        updateEntity: PropTypes.func,
        entityLabel: PropTypes.string,
        messageBoxName: PropTypes.shape({
            error: PropTypes.string,
            success: PropTypes.string,
        }),
        meta: PropTypes.shape({
            submitting: PropTypes.bool,
        }),
        disabled: PropTypes.bool,
        options: PropTypes.array,
        showWarning: PropTypes.bool,
    };

    return {
        entityDataMapKey: 'distributionInvoiceStatuses',
        entityLabel: 'invoice status',
        formName: 'Distribution Invoice Status',
        formValidationMessageBoxName: 'distributionInvoiceStatusFormValidationBox',
        resources: DISTRIBUTION_INVOICE_STATUS,
        showChangeLog: false,
        renderLabels: false,
        validate: validate,
        title: () => null,
        mutation: {
            createEntity: BulkCreateDistributionInvoiceStatuses,
            updateEntity: BulkUpdateDistributionInvoiceStatuses,
        },
        query: {
            getOptions: GetDistributionInvoiceStatusesForForm,
        },
        messages: {
            boxName: {
                delete: 'eventsIndex',
                success: 'event',
                error: 'event',
            },
            text: messages,
        },
        dataMap: {
            distributionInvoiceStatuses: {},
        },
        fields: {
            invoice_statuses: {
                component: renderDistributionInvoiceStatusFields,
                optionsKey: 'invoiceStatuses',
                props: {
                    optionsKey: 'invoiceStatuses',
                    name: 'invoice_statuses',
                    groups: null,
                },
            },
        },
    };
}
